import { message as staticMessage } from 'antd';
import type { MessageInstance } from 'antd/es/message/interface';

import { announce } from '@/a11y/liveRegion';

/**
 * Theme/context-aware message singleton. AntD warns when the static `message.*`
 * methods are used because they can't read the dynamic theme/context. We bind a
 * context-aware instance (from `App.useApp()`, set by <MessageBridge/>) once the
 * tree mounts, and proxy to it — call sites keep using `message.success(...)`.
 * Falls back to the static instance before the bridge initializes.
 */
let instance: MessageInstance | null = null;

export function setMessageInstance(m: MessageInstance): void {
  instance = m;
}

/**
 * Which toast kinds are announced, and how urgently (WCAG 2.1 — 4.1.3).
 * AntD 5.29 renders no `role="alert"` / `aria-live` on messages, so without
 * this every toast in the app is invisible to a screen reader: the user gets
 * no confirmation an action worked and no warning when it failed. Errors
 * interrupt; everything else waits for a pause.
 */
const ANNOUNCED: Record<string, 'polite' | 'assertive'> = {
  success: 'polite',
  info: 'polite',
  loading: 'polite',
  warning: 'assertive',
  error: 'assertive',
};

/** AntD accepts a bare string or a config object; the text lives in different
 *  places depending on which. Anything else isn't speakable and is skipped. */
function textOf(arg: unknown): string {
  if (typeof arg === 'string') return arg;
  if (arg && typeof arg === 'object' && 'content' in arg) {
    const content = (arg as { content: unknown }).content;
    if (typeof content === 'string') return content;
  }
  return '';
}

export const message = new Proxy(staticMessage as unknown as MessageInstance, {
  get(target, prop) {
    const src = (instance ?? target) as unknown as Record<PropertyKey, unknown>;
    const value = src[prop];
    if (typeof value !== 'function') return value;
    const fn = (value as (...args: unknown[]) => unknown).bind(src);
    const politeness = typeof prop === 'string' ? ANNOUNCED[prop] : undefined;
    if (!politeness) return fn;
    return (...args: unknown[]) => {
      announce(textOf(args[0]), politeness);
      return fn(...args);
    };
  },
});
